import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import { API_BASE, T } from '../constants';
import { fmtDateTime } from '../helpers';

/* ─────────────────────────────────────────────────────────────────────────────
 * CAV1Backtest
 *
 * Drives the /strategies/api/strategies/cav1/backtest/run/ endpoint and
 * displays a side-by-side comparison of the BASELINE rule set vs the
 * proposed WITH NEW RULE (LUX_LONG_12 closes shorts >20pts underwater).
 *
 *   1. Window summary banner
 *   2. Two stat cards — Baseline vs With New Rule
 *   3. Delta panel — what the new rule actually changed
 *   4. Trades closed by the new rule (the rule's actual fires)
 *   5. Full trade list (with-rule run), filterable by side / exit reason
 * ──────────────────────────────────────────────────────────────────────────── */

const RUN_URL = `${API_BASE}/strategies/api/strategies/cav1/backtest/run/`;

const fmtUSD = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${Number(n).toFixed(2)}%`);
const fmtNum = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));

const presets = [
  { label: 'All',     start: '',                     end: ''                     },
  { label: 'Jan–Mar', start: '2026-01-01T00:00:00',  end: '2026-04-01T00:00:00' },
  { label: 'April',   start: '2026-04-01T00:00:00',  end: '2026-05-01T00:00:00' },
];


export default function CAV1Backtest() {
  const [start, setStart]     = useState('');
  const [end, setEnd]         = useState('');
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  // filters for the trades table
  const [sideFilter, setSideFilter]     = useState('ALL');
  const [reasonFilter, setReasonFilter] = useState('ALL');

  const runBacktest = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (start) params.start = start;
      if (end)   params.end   = end;
      const res = await axios.get(RUN_URL, { params });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  };

  // run automatically once on mount with no filters
  useEffect(() => { runBacktest(); /* eslint-disable-next-line */ }, []);

  const filteredTrades = useMemo(() => {
    if (!data?.trades) return [];
    return data.trades.filter(t => {
      if (sideFilter   !== 'ALL' && t.side        !== sideFilter)   return false;
      if (reasonFilter !== 'ALL' && t.exit_reason !== reasonFilter) return false;
      return true;
    });
  }, [data, sideFilter, reasonFilter]);

  const reasonsInData = useMemo(() => {
    if (!data?.trades) return [];
    return Array.from(new Set(data.trades.map(t => t.exit_reason).filter(Boolean))).sort();
  }, [data]);

  return (
    <div style={styles.page}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={styles.header}>
        <Link to="/" style={styles.backLink}>← Dashboard</Link>
        <h1 style={styles.title}>CAV1 Backtest</h1>
        <span style={styles.subtitle}>Config A V1 LOCKED — historical replay vs new short-side LUX rule</span>
      </header>

      {/* ── Controls ───────────────────────────────────────────────────── */}
      <section style={styles.controls}>
        <div style={styles.presetRow}>
          <span style={styles.presetLabel}>Window:</span>
          {presets.map(p => (
            <button
              key={p.label}
              onClick={() => { setStart(p.start); setEnd(p.end); }}
              style={{
                ...styles.presetBtn,
                ...(start === p.start && end === p.end ? styles.presetBtnActive : {}),
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={styles.dateRow}>
          <label style={styles.dateLabel}>Start (ISO)</label>
          <input value={start} onChange={e => setStart(e.target.value)}
                 placeholder="2026-01-01T00:00:00" style={styles.input}/>
          <label style={styles.dateLabel}>End (ISO)</label>
          <input value={end} onChange={e => setEnd(e.target.value)}
                 placeholder="2026-04-01T00:00:00" style={styles.input}/>
          <button onClick={runBacktest} disabled={loading} style={styles.runBtn}>
            {loading ? 'Running…' : 'Run Backtest'}
          </button>
        </div>
      </section>

      {/* ── Errors ─────────────────────────────────────────────────────── */}
      {error && (
        <div style={styles.errorBox}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* ── Results ────────────────────────────────────────────────────── */}
      {data && (
        <>
          {/* Window summary */}
          <section style={styles.windowBox}>
            <div style={styles.windowItem}>
              <span style={styles.windowKey}>Window</span>
              <span style={styles.windowVal}>
                {data.window?.start ? fmtDateTime(data.window.start) : '—'}
                {' → '}
                {data.window?.end ? fmtDateTime(data.window.end) : '—'}
              </span>
            </div>
            <div style={styles.windowItem}>
              <span style={styles.windowKey}>Signals</span>
              <span style={styles.windowVal}>{fmtNum(data.window?.signal_count)}</span>
            </div>
            <div style={styles.windowItem}>
              <span style={styles.windowKey}>CONFIG_*</span>
              <span style={styles.windowVal}>{fmtNum(data.window?.config_signal_count)}</span>
            </div>
            <div style={styles.windowItem}>
              <span style={styles.windowKey}>LUX12</span>
              <span style={styles.windowVal}>{fmtNum(data.window?.lux12_signal_count)}</span>
            </div>
          </section>

          {/* Side-by-side stat cards */}
          <section style={styles.cardRow}>
            <SummaryCard title="Baseline" subtitle="Existing rules only" data={data.baseline} accent={T.textDim}/>
            <SummaryCard title="With New Rule" subtitle="+ LUX_LONG_12 short exit" data={data.with_rule} accent={T.bull}/>
          </section>

          {/* Delta */}
          <section style={styles.deltaBox}>
            <div style={styles.deltaTitle}>New rule impact</div>
            <div style={styles.deltaRow}>
              <DeltaItem label="Shorts closed by new rule" value={fmtNum(data.delta?.shorts_closed_by_new_rule)} />
              <DeltaItem label="P&L from those trades"     value={fmtUSD(data.delta?.new_rule_pnl_usd)}
                                                          color={Number(data.delta?.new_rule_pnl_usd) >= 0 ? T.bull : T.bear}/>
              <DeltaItem label="Net P&L change"            value={fmtUSD(data.delta?.total_pnl_usd)}
                                                          color={Number(data.delta?.total_pnl_usd) >= 0 ? T.bull : T.bear}/>
              <DeltaItem label="Win rate change"           value={`${Number(data.delta?.win_rate_pct) >= 0 ? '+' : ''}${fmtPct(data.delta?.win_rate_pct)}`}
                                                          color={Number(data.delta?.win_rate_pct) >= 0 ? T.bull : T.bear}/>
            </div>
            {data.diagnostics && (
              <div style={styles.diagRow}>
                <span style={styles.diagItem}>
                  baseline: skipped entries (position open)={data.diagnostics.baseline?.entries_skipped_position_open ?? 0},{' '}
                  exits dropped={data.diagnostics.baseline?.exits_dropped_no_position ?? 0}
                </span>
              </div>
            )}
          </section>

          {/* Trades closed by the new rule */}
          {data.new_rule_trades?.length > 0 && (
            <section style={styles.section}>
              <h2 style={styles.sectionTitle}>Shorts closed by new rule ({data.new_rule_trades.length})</h2>
              <TradeTable trades={data.new_rule_trades} compact />
            </section>
          )}

          {/* Full trade list */}
          <section style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>All trades ({filteredTrades.length} of {data.trades?.length || 0})</h2>
              <div style={styles.filterRow}>
                <label style={styles.filterLabel}>Side</label>
                <select value={sideFilter} onChange={e => setSideFilter(e.target.value)} style={styles.select}>
                  <option value="ALL">All</option>
                  <option value="LONG">LONG</option>
                  <option value="SHORT">SHORT</option>
                </select>
                <label style={styles.filterLabel}>Reason</label>
                <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value)} style={styles.select}>
                  <option value="ALL">All</option>
                  {reasonsInData.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <button
                  onClick={() => downloadBacktestCSV(filteredTrades, data.with_rule, data.window)}
                  style={styles.csvBtn}
                  title="Download filtered trades as CSV"
                >
                  ↓ CSV
                </button>
              </div>
            </div>
            <TradeTable trades={filteredTrades} />
          </section>
        </>
      )}

      {/* Loading state when no data yet */}
      {!data && loading && (
        <div style={styles.loadingBox}>Running backtest…</div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────── */
/* Sub-components                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

function SummaryCard({ title, subtitle, data, accent }) {
  if (!data) return null;
  const pnlColor = Number(data.total_pnl_usd) >= 0 ? T.bull : T.bear;
  return (
    <div style={{ ...styles.card, borderTopColor: accent }}>
      <div style={styles.cardHeader}>
        <span style={{ ...styles.cardTitle, color: accent }}>{title}</span>
        <span style={styles.cardSubtitle}>{subtitle}</span>
      </div>
      <div style={styles.statBig}>
        <span style={{ ...styles.statBigValue, color: pnlColor }}>{fmtUSD(data.total_pnl_usd)}</span>
        <span style={styles.statBigLabel}>Total P&L</span>
      </div>
      <div style={styles.statGrid}>
        <Stat label="Trades"        value={fmtNum(data.total_trades)}/>
        <Stat label="Win rate"      value={fmtPct(data.win_rate_pct)} color={data.win_rate_pct >= 60 ? T.bull : T.bear}/>
        <Stat label="Profit factor" value={data.profit_factor === null ? '—' : data.profit_factor.toFixed(2)}
              color={data.profit_factor >= 1.5 ? T.bull : T.bear}/>
        <Stat label="Wins"          value={fmtNum(data.wins)} color={T.bull}/>
        <Stat label="Losses"        value={fmtNum(data.losses)} color={T.bear}/>
        <Stat label="Avg P&L"       value={fmtUSD(data.avg_pnl_usd)}/>
        <Stat label="Max win"       value={fmtUSD(data.max_win_usd)} color={T.bull}/>
        <Stat label="Max loss"      value={fmtUSD(data.max_loss_usd)} color={T.bear}/>
      </div>
      {data.by_side && (
        <div style={styles.miniSection}>
          <div style={styles.miniTitle}>By side</div>
          {Object.entries(data.by_side).map(([side, v]) => (
            <div key={side} style={styles.miniRow}>
              <span style={styles.miniLabel}>{side}</span>
              <span style={styles.miniVal}>{v.count} trades · WR {fmtPct(v.win_rate_pct)} · {fmtUSD(v.pnl_usd)}</span>
            </div>
          ))}
        </div>
      )}
      {data.by_exit_reason && (
        <div style={styles.miniSection}>
          <div style={styles.miniTitle}>By exit reason</div>
          {Object.entries(data.by_exit_reason).map(([reason, v]) => (
            <div key={reason} style={styles.miniRow}>
              <span style={styles.miniLabel}>{reason}</span>
              <span style={styles.miniVal}>{v.count} · {fmtUSD(v.pnl_usd)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <span style={{ ...styles.statValue, ...(color ? { color } : {}) }}>{value}</span>
    </div>
  );
}

function DeltaItem({ label, value, color }) {
  return (
    <div style={styles.deltaItem}>
      <span style={styles.deltaLabel}>{label}</span>
      <span style={{ ...styles.deltaValue, ...(color ? { color } : {}) }}>{value}</span>
    </div>
  );
}

function downloadBacktestCSV(trades, summary, windowData) {
  if (!trades || trades.length === 0) return;

  const fmtET = ts => ts
    ? new Date(ts).toLocaleString('sv-SE', { timeZone: 'America/New_York' }) + ' ET'
    : '';

  const esc = v => {
    const s = String(v ?? '');
    return (s.includes(',') || s.includes('"') || s.includes('\n'))
      ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const sumRows = [
    ['# CAV1 Backtest Export', new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })],
    ['# Window', windowData?.start ? `${fmtET(windowData.start)} → ${fmtET(windowData.end)}` : 'All'],
    ['# Total Trades', summary?.total_trades ?? ''],
    ['# Win Rate', summary?.win_rate_pct != null ? `${Number(summary.win_rate_pct).toFixed(2)}%` : ''],
    ['# Total P/L (USD)', summary?.total_pnl_usd != null ? Number(summary.total_pnl_usd).toFixed(2) : ''],
    ['# Profit Factor', summary?.profit_factor != null ? Number(summary.profit_factor).toFixed(2) : ''],
    ['# Wins', summary?.wins ?? ''],
    ['# Losses', summary?.losses ?? ''],
    ['# Avg P/L (USD)', summary?.avg_pnl_usd != null ? Number(summary.avg_pnl_usd).toFixed(2) : ''],
    ['# Max Win (USD)', summary?.max_win_usd != null ? Number(summary.max_win_usd).toFixed(2) : ''],
    ['# Max Loss (USD)', summary?.max_loss_usd != null ? Number(summary.max_loss_usd).toFixed(2) : ''],
    [],
    ['#', 'Entry (ET)', 'Exit (ET)', 'Side', 'Grade', 'Contracts',
     'Entry Price', 'Exit Price', 'Exit Reason',
     'P/L pts', 'P/L USD', 'P/L %', 'Running P/L USD', 'Drawdown USD', 'Runup USD'],
  ];

  let runningPnl = 0, peakPnl = 0, troughPnl = 0;
  const tradeRows = trades.map((t, i) => {
    const pnlUsd = Number(t.pnl_usd) || 0;
    runningPnl += pnlUsd;
    peakPnl    = Math.max(peakPnl, runningPnl);
    troughPnl  = Math.min(troughPnl, runningPnl);
    const drawdown = peakPnl - runningPnl;
    const runup    = runningPnl - troughPnl;
    const ep  = Number(t.entry_price);
    const pts = Number(t.pnl_pts);
    const pct = (ep && !Number.isNaN(pts)) ? (pts / ep) * 100 : 0;
    return [
      i + 1,
      fmtET(t.entry_ts),
      fmtET(t.exit_ts),
      t.side || '',
      t.grade || '',
      t.contracts ?? '',
      ep ? ep.toFixed(2) : '',
      t.exit_price != null ? Number(t.exit_price).toFixed(2) : '',
      t.exit_reason || '',
      t.pnl_pts != null ? Number(t.pnl_pts).toFixed(2) : '',
      pnlUsd.toFixed(2),
      pct.toFixed(3),
      runningPnl.toFixed(2),
      drawdown.toFixed(2),
      runup.toFixed(2),
    ];
  });

  const csv = [
    ...sumRows.map(row => row.map(esc).join(',')),
    ...tradeRows.map(row => row.map(esc).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `cav1_backtest_${new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// pnl_pts is already direction-adjusted (long: exit-entry, short: entry-exit),
// so the percentage is just pts / entry_price regardless of side.
const pnlPctOf = t => {
  const ep = Number(t.entry_price);
  const pts = Number(t.pnl_pts);
  if (!ep || Number.isNaN(pts)) return 0;
  return (pts / ep) * 100;
};

function TradeTable({ trades, compact }) {
  const [sortKey, setSortKey] = useState(null);   // null = preserve incoming order
  const [sortDir, setSortDir] = useState('asc');  // 'asc' | 'desc'

  const onSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    if (!trades) return [];
    if (!sortKey) return trades;
    const arr = [...trades];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case 'side':        av = a.side || '';        bv = b.side || '';        break;
        case 'grade':       av = a.grade || '';       bv = b.grade || '';       break;
        case 'contracts':   av = Number(a.contracts) || 0; bv = Number(b.contracts) || 0; break;
        case 'entry_ts':    av = a.entry_ts || '';    bv = b.entry_ts || '';    break;
        case 'exit_ts':     av = a.exit_ts || '';     bv = b.exit_ts || '';     break;
        case 'exit_reason': av = a.exit_reason || ''; bv = b.exit_reason || ''; break;
        case 'pnl_pts':     av = Number(a.pnl_pts) || 0; bv = Number(b.pnl_pts) || 0; break;
        case 'pnl_usd':     av = Number(a.pnl_usd) || 0; bv = Number(b.pnl_usd) || 0; break;
        case 'pnl_pct':     av = pnlPctOf(a);            bv = pnlPctOf(b);            break;
        default: return 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [trades, sortKey, sortDir]);

  if (!trades || trades.length === 0) {
    return <div style={styles.emptyTable}>No trades match the current filters.</div>;
  }

  const headerCell = (key, label) => {
    const active = sortKey === key;
    const arrow  = active ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
    return (
      <th
        style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => onSort(key)}
        title="Click to sort"
      >
        {label}
        <span style={{ marginLeft: 4, color: active ? T.text : T.textMuted, fontSize: 9 }}>
          {arrow}
        </span>
      </th>
    );
  };

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.tr}>
            {headerCell('side',        'Side')}
            {headerCell('grade',       'Grade')}
            {headerCell('contracts',   'Cts')}
            {headerCell('entry_ts',    'Entry')}
            {headerCell('exit_ts',     'Exit')}
            {headerCell('exit_reason', 'Reason')}
            {headerCell('pnl_pts',     'Pts')}
            {headerCell('pnl_usd',     'P&L')}
            {headerCell('pnl_pct',     'P&L %')}
          </tr>
        </thead>
        <tbody>
          {sorted.map((t, i) => {
            const pnlColor  = Number(t.pnl_usd) >= 0 ? T.bull : T.bear;
            const sideColor = t.side === 'LONG' ? T.bull : T.bear;
            const pct       = pnlPctOf(t);
            return (
              <tr key={i} style={i % 2 ? styles.trAlt : styles.tr}>
                <td style={{ ...styles.td, color: sideColor, fontWeight: 700 }}>{t.side}</td>
                <td style={styles.td}>{t.grade || '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{t.contracts}</td>
                <td style={styles.tdSmall}>
                  <div>{fmtDateTime(t.entry_ts)}</div>
                  <div style={styles.tdSubtle}>@ {Number(t.entry_price).toFixed(2)}</div>
                </td>
                <td style={styles.tdSmall}>
                  <div>{t.exit_ts ? fmtDateTime(t.exit_ts) : <em style={styles.tdSubtle}>open</em>}</div>
                  <div style={styles.tdSubtle}>{t.exit_price !== null && t.exit_price !== undefined ? `@ ${Number(t.exit_price).toFixed(2)}` : ''}</div>
                </td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{t.exit_reason || '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', color: pnlColor }}>
                  {t.pnl_pts > 0 ? '+' : ''}{Number(t.pnl_pts).toFixed(2)}
                </td>
                <td style={{ ...styles.td, fontFamily: 'monospace', color: pnlColor, fontWeight: 700 }}>
                  {fmtUSD(t.pnl_usd)}
                </td>
                <td style={{ ...styles.td, fontFamily: 'monospace', color: pnlColor }}>
                  {pct > 0 ? '+' : ''}{pct.toFixed(3)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {compact && trades.length > 0 && (
        <div style={styles.tableFooter}>
          Net: <strong style={{ color: trades.reduce((s,t) => s + Number(t.pnl_usd || 0), 0) >= 0 ? T.bull : T.bear }}>
            {fmtUSD(trades.reduce((s,t) => s + Number(t.pnl_usd || 0), 0))}
          </strong>
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────── */
/* Styles                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

const styles = {
  page: {
    minHeight: '100dvh',
    padding: 'clamp(12px, 3vw, 20px) clamp(10px, 4vw, 28px)',
    maxWidth: 1500, margin: '0 auto',
    background: T.bg, color: T.text, fontFamily: "'Inter', sans-serif",
    overflowX: 'hidden',
  },
  header: { marginBottom: 20 },
  backLink: {
    display: 'inline-block', color: T.textDim, textDecoration: 'none',
    fontSize: 12, marginBottom: 8,
  },
  title: { fontSize: 24, fontWeight: 700, margin: 0, color: T.text },
  subtitle: { fontSize: 12, color: T.textDim, fontWeight: 400 },

  controls: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
    padding: 14, marginBottom: 16,
  },
  presetRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 },
  presetLabel: { fontSize: 11, color: T.textMuted, fontWeight: 600, marginRight: 4 },
  presetBtn: {
    padding: '4px 10px', background: 'transparent', border: `1px solid ${T.border}`,
    borderRadius: 3, fontSize: 11, fontWeight: 600, color: T.text, cursor: 'pointer',
  },
  presetBtnActive: { background: T.blueBg, borderColor: T.blueBorder, color: T.blue },
  dateRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  dateLabel: { fontSize: 11, color: T.textMuted, fontWeight: 600 },
  input: {
    padding: '6px 10px', background: T.surfaceHi, border: `1px solid ${T.border}`,
    borderRadius: 3, fontSize: 12, color: T.text, fontFamily: 'monospace',
    minWidth: 200,
  },
  runBtn: {
    padding: '7px 16px', background: T.bull, color: '#fff', border: 'none',
    borderRadius: 3, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    marginLeft: 'auto', flex: '0 0 auto',
  },

  errorBox: {
    background: T.bearBg, border: `1px solid ${T.bearBorder}`, color: T.bear,
    padding: 12, borderRadius: 4, marginBottom: 12, fontSize: 13,
  },
  loadingBox: {
    padding: 40, textAlign: 'center', color: T.textDim, fontSize: 14,
  },

  windowBox: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
    padding: 12, marginBottom: 14, display: 'flex', gap: 24, flexWrap: 'wrap',
  },
  windowItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  windowKey: { fontSize: 10, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' },
  windowVal: { fontSize: 13, color: T.text, fontFamily: 'monospace' },

  cardRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 14, marginBottom: 14 },
  card: {
    background: T.surface, border: `1px solid ${T.border}`, borderTop: '3px solid',
    borderRadius: 6, padding: 16,
  },
  cardHeader: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14 },
  cardTitle: { fontSize: 16, fontWeight: 700 },
  cardSubtitle: { fontSize: 11, color: T.textDim },

  statBig: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 14 },
  statBigValue: { fontSize: 32, fontWeight: 800, fontFamily: 'monospace', lineHeight: 1 },
  statBigLabel: { fontSize: 10, color: T.textMuted, fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.08em' },

  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 10, marginBottom: 12 },
  stat: { display: 'flex', flexDirection: 'column', gap: 2 },
  statLabel: { fontSize: 10, color: T.textMuted, fontWeight: 600 },
  statValue: { fontSize: 14, fontFamily: 'monospace', color: T.text, fontWeight: 700 },

  miniSection: { borderTop: `1px solid ${T.borderLight}`, paddingTop: 10, marginTop: 8 },
  miniTitle: { fontSize: 10, color: T.textMuted, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' },
  miniRow: { display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0' },
  miniLabel: { color: T.textDim, fontFamily: 'monospace' },
  miniVal: { color: T.text, fontFamily: 'monospace' },

  deltaBox: {
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
    padding: 14, marginBottom: 14,
  },
  deltaTitle: { fontSize: 12, color: T.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 },
  deltaRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 14 },
  deltaItem: { display: 'flex', flexDirection: 'column', gap: 4 },
  deltaLabel: { fontSize: 11, color: T.textDim, fontWeight: 500 },
  deltaValue: { fontSize: 18, fontWeight: 800, fontFamily: 'monospace', color: T.text },
  diagRow: { marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.borderLight}` },
  diagItem: { fontSize: 10, color: T.textMuted, fontFamily: 'monospace' },

  section: { marginTop: 18 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' },
  sectionTitle: { fontSize: 14, fontWeight: 700, margin: 0, color: T.text },
  filterRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' },
  filterLabel: { fontSize: 11, color: T.textMuted, fontWeight: 600 },
  select: {
    padding: '4px 8px', background: T.surfaceHi, border: `1px solid ${T.border}`,
    borderRadius: 3, fontSize: 11, color: T.text,
  },

  csvBtn: {
    padding: '4px 10px', background: 'transparent', border: `1px solid ${T.border}`,
    borderRadius: 3, fontSize: 11, fontWeight: 600, color: T.textDim, cursor: 'pointer',
  },
  tableWrap: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 },
  tr: { background: T.surface },
  trAlt: { background: T.surfaceHi },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${T.border}`, fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' },
  td: { padding: '6px 10px', borderBottom: `1px solid ${T.borderLight}`, color: T.text },
  tdSmall: { padding: '6px 10px', borderBottom: `1px solid ${T.borderLight}`, color: T.text, fontSize: 11 },
  tdSubtle: { color: T.textMuted, fontFamily: 'monospace', fontSize: 10 },
  emptyTable: { padding: 30, textAlign: 'center', color: T.textMuted, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6 },
  tableFooter: { padding: 10, borderTop: `1px solid ${T.border}`, fontSize: 12, textAlign: 'right' },
};
